import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Pane,
  SearchInput,
  Select,
  Spinner,
  TextInput,
} from "evergreen-ui";
import DataTable from "../../components/DataTable";
import useDebounce from "../../hooks/useDebounce";
import useControlHub from "../../hooks/useControlHub";

const EntryCheckbox = ({ entry, onChange }) => (
  <Checkbox
    aria-label="Sync Class Account with ControlHub"
    checked={entry.active}
    onChange={onChange}
  />
);

const EntryCustomNameTextbox = ({ entry, loading, onChange }) => {
  const [text, setText] = useState(entry["custom_name"]);
  const debouncedText = useDebounce(text, 500);

  useEffect(() => {
    onChange({ target: { value: debouncedText } });
  }, [debouncedText, onChange]);

  const handleTextChange = useCallback((event) => {
    setText(event.target.value);
  }, []);

  return (
    <Pane alignItems="center" display="flex" width="100%">
      <TextInput
        onChange={handleTextChange}
        maxLength={25}
        value={text || ""}
      />
      {loading ? <Spinner size={16} /> : null}
    </Pane>
  );
};

const AccountingClassesScreen = () => {
  const { api } = useControlHub();
  const [source, setSource] = useState();
  const [data, setData] = useState();
  const [classifications, setClassifications] = useState([]);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);

  const loadClassAccounts = useCallback(() => {
    api
      .getAccountingClassAccounts()
      .then((data) => {
        setSource(data);
      })
      .catch(() => {});
  }, [api]);

  useEffect(() => {
    loadClassAccounts();
  }, [loadClassAccounts]);

  useEffect(() => {
    if (source) {
      const _classificationsMap = source.reduce(
        (prev, entry) => ({ ...prev, [entry.type]: true }),
        {}
      );
      setClassifications(Object.keys(_classificationsMap));
    }
  }, [source]);

  useEffect(() => {
    if (source) {
      let _data = [...source];
      if (filter) {
        _data = _data.filter((entry) => entry.type === filter);
      }
      if (debouncedSearch) {
        const _term = debouncedSearch.toUpperCase();
        _data = _data.filter((entry) => {
          if (
            (entry.type && entry.type.toUpperCase().includes(_term)) ||
            (entry.number && entry.number.toUpperCase().includes(_term)) ||
            (entry.route && entry.route.toUpperCase().includes(_term)) ||
            (entry.name && entry.name.toUpperCase().includes(_term))
          ) {
            return true;
          }
          return false;
        });
      }
      setData(_data);
    } else {
      setData();
    }
  }, [debouncedSearch, filter, source]);

  const handleActiveChange = useCallback(
    (entry) => (event) => {
      if (event.target.checked) {
        api
          .activateAccountingAccounts({ accountIDs: [entry.id] })
          .then(() => {
            loadClassAccounts();
          })
          .catch(() => {});
      } else {
        api
          .deactivateAccountingAccounts({ accountIDs: [entry.id] })
          .then(() => {
            loadClassAccounts();
          })
          .catch(() => {});
      }
    },
    [api, loadClassAccounts]
  );

  const handleCustomNameChange = useCallback(
    (entry) => (event) => {
      if (entry["custom_name"] !== event.target.value) {
        api
          .setAccountingAccountCustomName({
            accountID: entry.id,
            customName: event.target.value,
          })
          .then(() => {})
          .catch(() => {});
      }
    },
    [api]
  );

  const handleFilterChange = useCallback((event) => {
    setFilter(event.target.value);
  }, []);

  const handleSearchChange = useCallback((event) => {
    setSearch(event.target.value);
  }, []);

  const handleActivateClick = useCallback(
    (entries) => () => {
      api
        .activateAccountingAccounts({
          accountIDs: entries.map((entry) => entry.id),
        })
        .then(() => {
          loadClassAccounts();
        })
        .catch(() => {});
    },
    [api, loadClassAccounts]
  );

  const handleDeactivateClick = useCallback(
    (entries) => () => {
      api
        .deactivateAccountingAccounts({
          accountIDs: entries.map((entry) => entry.id),
        })
        .then(() => {
          loadClassAccounts();
        })
        .catch(() => {});
    },
    [api, loadClassAccounts]
  );

  const tableColumns = useMemo(
    () => [
      {
        name: "Active",
        render: (entry) => (
          <EntryCheckbox entry={entry} onChange={handleActiveChange(entry)} />
        ),
        flex: "0 0 64px",
      },
      {
        key: "type",
        name: "Class Type",
        flex: "0.5",
      },
      {
        key: "number",
        name: "Class Number",
      },
      {
        key: "route",
        name: "Class Route",
      },
      {
        key: "name",
        name: "Class Name",
      },
      {
        name: "Custom Name",
        render: (entry) => (
          <EntryCustomNameTextbox
            entry={entry}
            onChange={handleCustomNameChange(entry)}
          />
        ),
      },
    ],
    [handleActiveChange, handleCustomNameChange]
  );

  if (!data) {
    return null;
  }

  return (
    <Pane>
      <Pane
        display="flex"
        flexWrap="wrap"
        justifyContent="space-between"
        marginY={16}
      >
        <Pane display="flex" marginY={8}>
          <SearchInput
            marginRight={8}
            onChange={handleSearchChange}
            placeholder="Search Classes..."
            value={search}
          />
          <Select marginLeft={8} onChange={handleFilterChange} value={filter}>
            <option value="">All</option>
            {classifications.map((classification) => (
              <option
                key={`Classification-${classification}`}
                value={classification}
              >
                {classification}
              </option>
            ))}
          </Select>
        </Pane>
        <Pane display="flex" marginY={8}>
          <Button marginRight={8} onClick={handleDeactivateClick(data)}>
            Deactivate Classes
          </Button>
          <Button marginLeft={8} onClick={handleActivateClick(data)}>
            Activate Classes
          </Button>
        </Pane>
      </Pane>
      <DataTable columns={tableColumns} data={data} />
    </Pane>
  );
};

export default AccountingClassesScreen;
